import { useState, useCallback, useMemo, useEffect } from 'react'

import type { CommentData } from '../context/CommentContext'

import { useLoadCommentContext } from './useLoadCommentContext'

const CONTEXT_COMMENTS_INCREMENT = 5

type UseContextCommentsReturn = {
  // State
  contextComments: CommentData[]
  showContext: boolean
  visibleCount: number
  isLoading: boolean
  showSpinner: boolean

  // Computed
  hasMore: boolean
  visibleContextComments: CommentData[]
  showContextComments: boolean

  // Actions
  handleExpandContext: () => Promise<void>
  handleCollapseContext: () => void
  handleShowMoreContext: () => void
}

export const useContextComments = (
  commentId: string,
): UseContextCommentsReturn => {
  const { loadContext, isLoading, showSpinner } = useLoadCommentContext()
  const [contextComments, setContextComments] = useState<CommentData[]>([])
  const [showContext, setShowContext] = useState(false)
  const [visibleCount, setVisibleCount] = useState(CONTEXT_COMMENTS_INCREMENT)

  const hasMore = contextComments.length > visibleCount
  const visibleContextComments = useMemo(
    () => contextComments.slice(-visibleCount),
    [contextComments, visibleCount],
  )
  const showContextComments = showContext && contextComments.length > 0

  // Reset when comment changes
  useEffect(() => {
    setShowContext(false)
    setContextComments([])
    setVisibleCount(CONTEXT_COMMENTS_INCREMENT)
  }, [commentId])

  const handleExpandContext = useCallback(async (): Promise<void> => {
    if (showContext) {
      setShowContext(false)
      return
    }

    try {
      const context = await loadContext(commentId)
      if (context) {
        setContextComments(context)
        setShowContext(true)
      }
    } catch {
      // TODO: surface a non-blocking notification if available in this app
      setShowContext(false)
    }
  }, [showContext, loadContext, commentId])

  const handleCollapseContext = useCallback((): void => {
    setShowContext(false)
    setVisibleCount(CONTEXT_COMMENTS_INCREMENT)
  }, [])

  const handleShowMoreContext = useCallback((): void => {
    setVisibleCount((prev) => prev + CONTEXT_COMMENTS_INCREMENT)
  }, [])

  return {
    contextComments,
    showContext,
    visibleCount,
    isLoading,
    showSpinner,
    hasMore,
    visibleContextComments,
    showContextComments,
    handleExpandContext,
    handleCollapseContext,
    handleShowMoreContext,
  }
}
